// Purpose: Store for managing pagination and sorting of table data.
// Specifically designed to meld well with b-table and b-pagination components.
//
// This store handles a few related responsibilities:
//   - Tracking typical pagination state like current page and total item count in dataset
//   - Tracking sorting state
//   - Tracking the index of a selected item on the current page. We refer to
//     selecting the next or previous item as "scrolling"
//
// Note that since we just have a single pagination store, it's presumed that
// there will only be one table per page. Currently we don't have any specific
// plans to have multiple tables on a single page, but if that changes we can pretty
// easily evolve this solution, one option being to split it into one store per table
// e.g. RecipientPaginationStore, SurveyPaginationStore, etc. If we go that route
// we can follow the pattern the pinia maintainer suggested here:
// https://github.com/vuejs/pinia/discussions/771
import { defineStore } from 'pinia'

export const usePaginationStore = defineStore('PaginationStore', {
  state: () => {
    return {
      // Current page is 1-indexed not 0-indexed
      currentPage: 1,
      perPage: 25,
      totalRows: 0,
      sortBy: null,
      sortDesc: false,
    }
  },
  getters: {
    totalPages() {
      return Math.ceil(this.totalRows / this.perPage)
    },
    currentPageSize() {
      return Math.min(this.perPage, this.totalRows - (this.currentPage - 1) * this.perPage)
    },
    // Index of first item on page relative to whole data set (1-indexed)
    startOfCurrentPage() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    // Index of last item on page relative to whole data set (1-indexed)
    endOfCurrentPage() {
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
    paginationParams() {
      return {
        page: this.currentPage,
        per_page: this.perPage,
      }
    },
  },
  actions: {
    nextPage() {
      if (this.currentPage < this.totalPages) {
        ++this.currentPage
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        --this.currentPage
      }
    },
  },
})
